<template>
  <c-loading-area :loading="loading">
    <v-card-title>
      {{ $t('page.manageFiling.abbreviatedFiling.docketEntryDetails.title') }}
    </v-card-title>
    <v-card-subtitle>
      {{ $t('page.manageFiling.abbreviatedFiling.docketEntryDetails.description') }}
    </v-card-subtitle>
    <v-card-text class="pt-2">
      <v-row no-gutters>
        <v-col cols="12">
          <c-textarea
            v-model="bean.docketEntry.docketEntryNote"
            :label="$t('page.manageFiling.abbreviatedFiling.docketEntryDetails.filingStatement')"
            maxlength="2000"
            outlined
            hide-details="auto"
            aria-describedby="1a1e42d2-8321-48a7-ab9f-f1347c879354"/>
          <field-description id="1a1e42d2-8321-48a7-ab9f-f1347c879354"
                             :message-key="filingStatementDescriptionKey"/>
        </v-col>
        <v-col cols="12">
          <c-textarea
            v-model="bean.filing.filingNote"
            :label="$t('page.manageFiling.abbreviatedFiling.docketEntryDetails.comments')"
            maxlength="2000"
            outlined
            hide-details="auto"
            aria-describedby="b199aeb7-6b7d-40d2-b956-8f62a2b25b32"/>
          <field-description id="b199aeb7-6b7d-40d2-b956-8f62a2b25b32"
                             message-key="page.manageFiling.abbreviatedFiling.docketEntryDetails.comments.description"/>
        </v-col>
        <step-details-sandbox :original-bean="bean" @customFieldsMap="setCustomFieldsMap"/>
        <v-col cols="12">
          <section-separator :title="$t('page.manageFiling.abbreviatedFiling.section.fileAttachment')" class="mb-5"/>
        </v-col>
        <v-col cols="12">
          <template v-if="bean.docketEntryDocument.documentLinkID === null">
            <c-file-input
              v-model="file"
              :label="$t('page.manageFiling.abbreviatedFiling.docketEntryDetails.file')"
              key="file"
              outlined
              hide-details="auto"
              aria-describedby="7b1ebb78-322f-450d-9c14-ea6c85b022cb"/>
            <field-description id="7b1ebb78-322f-450d-9c14-ea6c85b022cb">
              {{ $t(docketEntryDocumentFileDescriptionKey) }}
              <button type="button" class="link-button" @click="openFileTypesPopup">
                {{ $t('page.manageFiling.abbreviatedFiling.docketEntryDetails.file.fileTypes') }}
              </button>
            </field-description>
            <file-types-popup v-model="fileTypesPopupVisible" :fileType="CONST.DOCUMENT_TYPE_FILING"/>
          </template>
          <template v-else>
            <div class="mb-6">
              <c-btn small :href="viewDocumentURL" target="_blank">
                {{ $t('global.viewFile') }}
              </c-btn>
              <c-btn small @click="queueDocumentForRemoval">
                {{ $t('global.removeFile') }}
              </c-btn>
            </div>
          </template>
        </v-col>
      </v-row>

      <v-row v-if="filingFeesTotal !== 0" no-gutters>
        <v-col cols="12">
          <section-separator :title="$t('page.manageFiling.abbreviatedFiling.section.fees')" class="mb-5"/>
          <dl class="d-flex align-center" style="gap:1em">
            <dt>{{ $t('page.manageFiling.summary.filingFeesTotal') }}</dt>
            <dd>
              <button type="button" class="link-button" @click="filingFeesPopupVisible = true;">
                {{ filingFeesTotal | formatCurrency }}
              </button>
            </dd>
          </dl>

          <c-autocomplete
            v-model="bean.filing.feeWaiverReasonID"
            :label="$t('page.manageFiling.abbreviatedFiling.feeWaiver')"
            :items="feeWaiverReasons"
            item-text="displayName"
            item-value="resourceID"
            class="mt-4"
            outlined
            hide-details="auto"
            aria-describedby="a66c2100-710c-4e10-a5b5-6b4601aade9c">
            <template #item="{ item }">
              <v-list-item-content class="d-block">
                <v-list-item-title class="font-weight-medium">
                  {{ item.displayName }}
                </v-list-item-title>
                <div class="d-flex text-minimum-gray font-95" style="max-width:70ch">
                  {{ item.description }}
                </div>
              </v-list-item-content>
            </template>
          </c-autocomplete>
          <field-description id="a66c2100-710c-4e10-a5b5-6b4601aade9c"
                             message-key="page.manageFiling.abbreviatedFiling.feeWaiver.description"/>

          <filing-fees-popup v-model="filingFeesPopupVisible" :docketEntryID="leadDocketEntry.resourceID"/>
        </v-col>
      </v-row>

    </v-card-text>
    <v-card-actions class="justify-end mt-5">
      <c-btn type="primary" @click="save">
        {{ $t('global.save') }}
      </c-btn>
      <template v-if="editing">
        <c-btn @click="$emit('cancel')">
          {{ $t('global.cancel') }}
        </c-btn>
      </template>
    </v-card-actions>
  </c-loading-area>
</template>

<script>
import _ from 'lodash';
import axios from 'axios';
import WorkflowStepMixin from '@/mixins/workflowStep';
import FileTypesPopup from '@components/support/FileTypesPopup';
import FilingFeesPopup from '@components/support/manageFiling/FilingFeesPopup';
import { saveDocument } from '@/application/application';
import { validateFile } from '@/application/utility';
import Filing from '@/application/filing';
import { useFilingStore } from '@/stores/filingStore';
import { useApplicationStore } from '@/stores/applicationStore';
import StepDetailsSandbox from '@components/support/manageFiling/abbreviated/StepDetailsSandbox.vue';

const filingStore = useFilingStore();
const applicationStore = useApplicationStore();

export default {
  components: { StepDetailsSandbox, FileTypesPopup, FilingFeesPopup },
  mixins: [ WorkflowStepMixin ],
  props: {
    editing: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      file: null,
      filingFeesPopupVisible: false,
      fileTypesPopupVisible: false,
      removeDocketEntryDocumentID: null,
      feeWaiverReasons: []
    };
  },
  computed: {
    leadDocketEntry () {
      return filingStore.filing.leadDocketEntry;
    },
    filingFeesTotal () {
      return this.leadDocketEntry.feeAmount;
    },
    viewDocumentURL () {
      return this.$cove.getAPI({
        name: 'api.dms.document',
        params: { documentLinkID: this.bean.docketEntryDocument.documentLinkID }
      });
    },
    isFilingStatementRequired () {
      return Filing.getAbbreviatedFilingConfig().requireFilingStatement;
    },
    isDocumentRequired () {
      return Filing.getAbbreviatedFilingConfig().requireDocument;
    },
    isFilingStatementOrDocumentRequired () {
      return Filing.getAbbreviatedFilingConfig().requireFilingStatementOrDocument;
    },
    filing () {
      return filingStore.filing;
    },
    filingStatement () {
      return this.bean.docketEntry.docketEntryNote;
    },
    comments () {
      return this.bean.filing.filingNote;
    },
    filingStatementDescriptionKey () {
      let config = Filing.getAbbreviatedFilingConfig();
      if (config.requireFilingStatementOrDocument)
      {
        return 'page.manageFiling.abbreviatedFiling.docketEntryDetails.filingStatementOrDocument.description';
      }
      else if (config.requireFilingStatement)
      {
        return 'page.manageFiling.abbreviatedFiling.docketEntryDetails.filingStatement.description';
      }
      else
      {
        return '';
      }
    },
    docketEntryDocumentFileDescriptionKey () {
      let config = Filing.getAbbreviatedFilingConfig();
      if (config.requireFilingStatementOrDocument)
      {
        return 'page.manageFiling.abbreviatedFiling.docketEntryDetails.filingStatementOrDocument.description';
      }
      else if (config.requireDocument)
      {
        return 'page.manageFiling.abbreviatedFiling.docketEntryDetails.file.description';
      }
      else
      {
        return '';
      }
    }
  },
  methods: {
    setCustomFieldsMap (values) {
      Filing.setAbbreviatedFilingCustomFieldsMap(this.bean, values);
    },
    loadData () {
      let promises = [
        applicationStore.loadFileTypes(this.CONST.DOCUMENT_TYPE_FILING),
        this.loadFeeWaiverReasons()
      ];

      return Promise.all(promises);
    },
    loadFeeWaiverReasons () {
      return axios.get(this.$cove.getAPI({ name: 'api.filing.feeWaiverReasons' }))
        .then((response) => {
          this.feeWaiverReasons = _.get(response, 'data._embedded.results', []);
        });
    },
    validate () {
      this.$cove.validation.clear();

      if (this.isFilingStatementRequired && _.isNil(this.filingStatement) )
      {
        this.$cove.validation.addErrorText('filingStatement', 'Filing statement is required');
      }

      if (this.isDocumentRequired && _.isNil(this.file)
          && _.isNil(this.bean.docketEntryDocument.documentLinkID))
      {
        this.$cove.validation.required(this.file, 'document.file', 'file');
      }

      if (this.isFilingStatementOrDocumentRequired && _.isNil(this.filingStatement)
          && _.isNil(this.file) && _.isNil(this.bean.docketEntryDocument.documentLinkID))
      {
        this.$cove.validation.addErrorText('filingStatementOrDocument',
          'Either Filing Statement or Docket Entry Document is required');
      }

      Filing.validateAbbreviatedFilingCustomFieldsMap(this.bean);

      if (!_.isNil(this.file))
      {
        validateFile(this.file, this.CONST.DOCUMENT_TYPE_FILING, 'page.manageAnnouncement.field.file', 'file');
      }

      return !this.$cove.validation.hasErrors();
    },
    save () {
      let isValid = this.validate();
      if (!isValid)
      {
        this.scrollTo(this.CONST.ERROR_CONTAINER_SELECTOR);
      }
      else
      {
        this.saveNewDocumentAndRemoveOldDocumentIfNecessary()
          .then(() => {
            this.$emit('save', this.bean);
          });
      }
    },
    saveNewDocumentAndRemoveOldDocumentIfNecessary ()
    {
      let promise = Promise.resolve();
      if (this.file !== null)
      {
        // New document has been added
        this.$cove.blockNow();
        promise = saveDocument(this.CONST.SYSTEM_TABLE_DOCKET_ENTRY_DOCUMENT, 'Filing Document', this.file)
          .then((value) => {
            this.bean.docketEntryDocument.isNew = true;
            this.bean.docketEntryDocument.documentLinkID = value;

            // we'll wait to do the removal until at least the new document has saved successfully
            if (this.removeDocketEntryDocumentID !== null)
            {
              return this.removeDocument();
            }
            else
            {
              return Promise.resolve();
            }
          });
      }
      else if (this.removeDocketEntryDocumentID !== null)
      {
        // Current document has been removed, no new document to replace it
        this.$cove.blockNow();
        promise = this.removeDocument();
      }

      return promise
        .finally(() => {
          this.$cove.unblock();
        });
    },
    queueDocumentForRemoval () {
      this.bean.docketEntryDocument.documentLinkID = null;
      this.removeDocketEntryDocumentID = this.bean.docketEntryDocument.docketEntryDocumentID;
    },
    removeDocument () {
      return Filing.removeDocketEntryDocument(this.removeDocketEntryDocumentID);
    },
    openFileTypesPopup () {
      this.fileTypesPopupVisible = true;
    }
  }
};
</script>